import { FormEvent, useState } from 'react';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import IssueFilePicker from '../IssueFilePicker/IssueFilePicker';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';

const todayIso = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export type AddIssuePayload = {
  description: string;
  severity: string;
  issue_date: string;
  comment: string;
  files: File[];
};

type Props = {
  onClose: () => void;
  onConfirm: (data: AddIssuePayload) => void | Promise<void>;
};

const AddIssueModal = ({ onClose, onConfirm }: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const [form, setForm] = useState({
    description: '',
    severity: '',
    issue_date: todayIso(),
    comment: '',
  });
  const [files, setFiles] = useState<File[]>([]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.description.trim()) {
      showError('Укажите описание неисправности');
      return;
    }
    if (!form.issue_date) {
      showError('Укажите дату неисправности');
      return;
    }
    if (form.issue_date > todayIso()) {
      showError('Дата неисправности не может быть позже сегодняшнего дня');
      return;
    }
    void runSubmit(() =>
      onConfirm({
        description: form.description.trim(),
        severity: form.severity || 'medium',
        issue_date: form.issue_date,
        comment: form.comment.trim(),
        files,
      })
    );
  };

  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>Добавить неисправность</h3>
          <div className="modal-body">
            <label>Описание</label>
            <input
              type="text"
              placeholder="Описание неисправности"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              required
            />
            <label>Степень важности</label>
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
            >
              <option value="" disabled>
                Степень важности
              </option>
              <option value="low">Низкая</option>
              <option value="medium">Средняя</option>
              <option value="high">Высокая</option>
            </select>
            <label>Дата</label>
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) =>
                setForm({ ...form, issue_date: e.target.value })
              }
              max={todayIso()}
              required
            />
            <label>Комментарий</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этой неисправности..."
              rows={5}
            />
            <IssueFilePicker files={files} onChange={setFiles} />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : 'Добавить'}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddIssueModal;
